import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BASE_PRICES, MAX_MARKET_ORDER_QTY, MAX_TARIFF_RATE, TRADABLE_RESOURCES, formatDate,
} from '@pk/shared';
import type { Id, MarketView, TitleView, TradableResource } from '@pk/shared';
import { useCommand, useGuild, useMarket, useTitle } from '../../api/hooks';
import { Badge, Btn, Collapsible, Empty, ErrorBox, Inline, Sheet, Tag } from '../../components/ui';
import { IconChevron, ResourceIcon } from '../../components/icons';
import { Meta, NumField, Row, SheetLayout, Skeleton } from './shared';

export type CityRow = {
  cityId: Id; name: string; holderHouseName: string | null; development: number; tariffRate: number; orderCount: number; roadDays: number | null;
  myGuildXp?: number; myGuildRank?: number; nextElectionDay?: number;
};

const pct = (r: number) => Math.round(r * 100);

export function CityList({ cities, onPick }: { cities: CityRow[]; onPick: (id: Id) => void }) {
  const { t } = useTranslation();
  if (cities.length === 0) return <Empty>{t('trade.cities.none')}</Empty>;
  const rows = [...cities].sort((a, b) => (a.roadDays ?? 99) - (b.roadDays ?? 99)); // near first, unreachable last
  return (
    <section className="tr-section">
      <p className="tr-help">{t('eco.markets.help')}</p>
      <div className="tr-list">
        {rows.map((c) => (
          <Row key={c.cityId} onClick={() => onPick(c.cityId)}
            title={<>{c.name}{c.orderCount > 0 && <Tag tone="gold">{t('trade.cities.orders', { count: c.orderCount })}</Tag>}{(c.myGuildRank ?? 0) > 0 && <Tag tone="green">{t(`trade.guild.rank.${c.myGuildRank}`)}</Tag>}</>}
            meta={<Meta items={[
              c.roadDays != null ? t('eco.away', { count: c.roadDays }) : t('eco.noRoad'),
              t('eco.tariffPct', { pct: pct(c.tariffRate) }),
              t('eco.devN', { n: c.development }),
              c.holderHouseName ? t('eco.heldBy', { name: c.holderHouseName }) : null,
            ]} />}
            trail={<IconChevron className="tr-chev" />} />
        ))}
      </div>
    </section>
  );
}

/** Bottom sheet for one city: goods, your orders, guild and election, tariff for the holder. */
export function CitySheet({ cityId, name, actor, houseId, gold, onClose, focusGuild }: {
  cityId: Id; name: string; actor: Id; houseId: Id; gold: number; onClose: () => void; focusGuild?: boolean;
}) {
  const { t } = useTranslation();
  const market = useMarket(cityId);
  const [order, setOrder] = useState<{ side: 'buy' | 'sell'; resource: TradableResource } | null>(null);
  const m = market.data;
  return (
    <Sheet open onClose={onClose} fixed title={name}>
      {market.isLoading ? <SheetLayout><Skeleton rows={4} /></SheetLayout>
        : market.isError || !m ? <SheetLayout><ErrorBox error={market.error} onRetry={() => market.refetch()} /></SheetLayout>
        : order
          ? <OrderForm market={m} actor={actor} gold={gold} initial={order} onBack={() => setOrder(null)} onDone={() => setOrder(null)} />
          : <CityBody market={m} actor={actor} houseId={houseId} focusGuild={focusGuild} onOrder={(side, resource) => setOrder({ side, resource })} />}
    </Sheet>
  );
}

function trend(last: number | undefined, base: number): 'up' | 'down' | null {
  if (last == null || last === base) return null;
  return last > base ? 'up' : 'down';
}

function CityBody({ market: m, actor, houseId, focusGuild, onOrder }: { market: MarketView; actor: Id; houseId: Id; focusGuild?: boolean; onOrder: (s: 'buy' | 'sell', r: TradableResource) => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const guild = useGuild(m.cityId);
  const title = useTitle(m.cityId); // a city's id IS its city-rank title id; election rides on this view.
  const isHolder = m.holderHouseId === houseId;
  const stockText = Object.entries(m.makerStock).map(([r, n]) => `${t(`resource.${r}`)}: ${n}`).join(', ');

  return (
    <SheetLayout foot={<Btn variant="primary" block onClick={() => onOrder('buy', 'food')}>{t('eco.sheet.newOrder')}</Btn>}>
      <div className="tr-chips-meta">
        <Badge>{m.roadDays != null ? t('eco.away', { count: m.roadDays }) : t('eco.noRoad')}</Badge>
        <Badge>{t('eco.tariffPct', { pct: pct(m.tariffRate) })}</Badge>
        <Badge>{t('eco.devN', { n: m.development })}</Badge>
        {m.holderHouseName && <Badge tone="blue">{t('eco.heldBy', { name: m.holderHouseName })}</Badge>}
      </div>
      {m.deliveryDays != null && <p className="tr-help">{t('eco.sheet.caravanTakes', { count: m.deliveryDays })}</p>}

      <h4 className="tr-h">{t('eco.sheet.goods')}</h4>
      <p className="tr-help">{t('eco.sheet.goodsHelp')}</p>
      <div className="tr-list">
        {TRADABLE_RESOURCES.map((r) => {
          const q = m.quotes[r];
          const last = m.lastPrices[r];
          const tr = trend(last, q.base);
          return (
            <div key={r} className="tr-row tr-good">
              <span className="tr-row-lead" aria-hidden="true"><ResourceIcon k={r} /></span>
              <span className="tr-row-main">
                <span className="tr-row-title">
                  {t(`resource.${r}`)}
                  {tr && <span className={`tr-trend ${tr}`} title={t(`eco.sheet.trend.${tr}`)} aria-label={t(`eco.sheet.trend.${tr}`)}>{tr === 'up' ? '▲' : '▼'}</span>}
                </span>
                <span className="tr-row-meta">
                  <Meta items={[
                    t('eco.sheet.buyAt', { n: q.ask }),
                    t('eco.sheet.sellAt', { n: q.bid }),
                    m.makerStock[r] != null ? t('eco.sheet.stock', { n: m.makerStock[r] }) : null,
                    last != null ? t('eco.sheet.last', { n: last }) : null,
                  ]} />
                </span>
              </span>
              <span className="tr-good-act">
                <Btn small variant="primary" onClick={() => onOrder('buy', r)}>{t('trade.quote.buy')}</Btn>
                <Btn small onClick={() => onOrder('sell', r)}>{t('trade.quote.sell')}</Btn>
              </span>
            </div>
          );
        })}
      </div>
      <p className="tr-help">{t('eco.sheet.makerLine', { gold: m.makerGold })} {stockText && <span className="tr-faint">{t('trade.market.makerStock', { stock: stockText })}</span>}</p>

      <h4 className="tr-h">{t('trade.market.myOrders')}{m.myOrders.length > 0 && <span className="tr-count">{m.myOrders.length}</span>}</h4>
      {m.myOrders.length === 0 ? <p className="tr-help">{t('trade.market.noMyOrders')}</p> : (
        <div className="tr-list">
          {m.myOrders.map((o) => (
            <Row key={o.id} tone={o.side === 'buy' ? 'green' : 'red'} lead={<ResourceIcon k={o.resource} />}
              title={<><Tag tone={o.side === 'buy' ? 'green' : 'red'}>{t(`trade.market.side.${o.side}`)}</Tag> {t('eco.sheet.orderLine', { qty: o.remaining, good: t(`resource.${o.resource}`), price: o.unitPrice })}</>}
              meta={<Meta items={[
                o.escrowGold != null ? t('eco.sheet.escrowG', { n: o.escrowGold }) : o.escrowUnits != null ? t('eco.sheet.escrowU', { n: o.escrowUnits }) : null,
                t('eco.sheet.expires', { date: formatDate(o.expiresDay) }),
              ]} />}
              trail={<Btn small variant="danger" busy={cmd.pending} onClick={() => { void cmd.run({ type: 'cancel_market_order', actorId: actor, orderId: o.id }); }}>{t('trade.market.cancel')}</Btn>} />
          ))}
        </div>
      )}
      <Inline error={cmd.error} />

      <div className="tr-stack">
        <Collapsible title={t('trade.market.orders')} summary={m.orders.length}>
          {m.orders.length === 0 ? <p className="tr-help">{t('trade.market.noOrders')}</p> : (
            <div className="tr-list">
              {m.orders.map((o) => (
                <Row key={o.id} lead={<ResourceIcon k={o.resource} />}
                  title={<><Tag tone={o.side === 'buy' ? 'green' : 'red'}>{t(`trade.market.side.${o.side}`)}</Tag> {t('eco.sheet.orderLine', { qty: o.remaining, good: t(`resource.${o.resource}`), price: o.unitPrice })}</>}
                  meta={o.houseName} />
              ))}
            </div>
          )}
        </Collapsible>

        <GuildBlock guild={guild} focus={focusGuild} />
        {title.data?.election && <ElectionBlock cityId={m.cityId} election={title.data.election} actor={actor} />}
        {isHolder && <TariffBlock market={m} actor={actor} />}
      </div>
    </SheetLayout>
  );
}

/** Order form: pick side + good, set quantity and price with steppers; the footer always shows the total and the submit button. */
export function OrderForm({ market: m, actor, gold, initial, lockSide, onBack, onDone, backLabel }: {
  market: MarketView; actor: Id; gold: number; initial: { side: 'buy' | 'sell'; resource: TradableResource };
  lockSide?: 'buy'; onBack: () => void; onDone: () => void; backLabel?: string;
}) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [side, setSide] = useState<'buy' | 'sell'>(lockSide ?? initial.side);
  const [resource, setResource] = useState<TradableResource>(initial.resource);
  const [qty, setQty] = useState(10);
  const [price, setPrice] = useState(initial.side === 'buy' || lockSide ? m.quotes[initial.resource].ask : m.quotes[initial.resource].bid);

  const makerPrice = (s: 'buy' | 'sell', r: TradableResource) => (s === 'buy' ? m.quotes[r].ask : m.quotes[r].bid);
  const valid = qty >= 1 && qty <= MAX_MARKET_ORDER_QTY && price >= 1;
  const cost = qty * price;
  const poor = side === 'buy' && cost > gold;
  const place = async () => {
    if (!valid) return;
    const res = await cmd.run({ type: 'place_market_order', actorId: actor, cityId: m.cityId, side, resource, quantity: qty, unitPrice: price });
    if (res.ok) onDone();
  };

  return (
    <SheetLayout foot={
      <>
        <div className="tr-sum" aria-live="polite">
          <span className="tr-sum-main">{side === 'buy' ? t('eco.order.cost', { gold: cost }) : t('eco.order.units', { count: qty })}</span>
          <span className={`tr-sum-sub ${poor ? 'warn' : ''}`}>
            {poor ? t('eco.order.tooPoor', { gold }) : side === 'buy'
              ? (m.deliveryDays != null ? t('trade.market.eta', { count: m.deliveryDays }) : t('eco.order.youHave', { gold }))
              : t('eco.order.sellNote')}
          </span>
        </div>
        <Inline error={cmd.error} />
        <div className="tr-foot-row">
          <Btn variant="quiet" onClick={onBack}>{backLabel ?? t('eco.order.back')}</Btn>
          <Btn variant="primary" busy={cmd.pending} disabled={!valid} onClick={() => { void place(); }}>{t('trade.market.placeBtn')}</Btn>
        </div>
      </>
    }>
      {!lockSide && (
        <div className="tr-side" role="tablist">
          {(['buy', 'sell'] as const).map((s) => (
            <button key={s} type="button" role="tab" aria-selected={side === s} className={`${s} ${side === s ? 'on' : ''}`}
              onClick={() => { setSide(s); setPrice(makerPrice(s, resource)); }}>{t(`trade.market.side.${s}`)}</button>
          ))}
        </div>
      )}
      <span className="field-label">{t('eco.order.good')}</span>
      <div className="tr-goods">
        {TRADABLE_RESOURCES.map((r) => (
          <button key={r} type="button" className={`tr-good-pick ${resource === r ? 'on' : ''}`} aria-pressed={resource === r}
            onClick={() => { setResource(r); setPrice(makerPrice(side, r)); }}>
            <ResourceIcon k={r} /><span>{t(`resource.${r}`)}</span>
          </button>
        ))}
      </div>
      <NumField label={t('trade.market.qty')} value={qty} onChange={setQty} min={1} max={MAX_MARKET_ORDER_QTY}
        chips={[{ label: '10', value: 10 }, { label: '25', value: 25 }, { label: '50', value: 50 }, { label: t('eco.order.max'), value: MAX_MARKET_ORDER_QTY }]} />
      <NumField label={t('trade.market.unitPrice')} value={price} onChange={setPrice} min={1} max={9999} unit="g"
        chips={[{ label: t('eco.order.marketPrice', { n: makerPrice(side, resource) }), value: makerPrice(side, resource) }, { label: t('eco.order.basePrice', { n: BASE_PRICES[resource] }), value: BASE_PRICES[resource] }]}
        hint={side === 'buy' ? t('trade.market.priceHint.buy', { ask: m.quotes[resource].ask }) : t('trade.market.priceHint.sell', { bid: m.quotes[resource].bid })} />
    </SheetLayout>
  );
}

function TariffBlock({ market, actor }: { market: MarketView; actor: Id }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const maxPct = pct(MAX_TARIFF_RATE);
  const [rate, setRate] = useState(pct(market.tariffRate));
  return (
    <Collapsible title={t('trade.tariff.label')} summary={`${pct(market.tariffRate)}%`}>
      <NumField label={t('trade.tariff.label')} value={rate} onChange={setRate} min={0} max={maxPct} unit="%"
        chips={[0, 5, 10, maxPct].filter((v, i, a) => a.indexOf(v) === i).map((v) => ({ label: `${v}%`, value: v }))}
        hint={t('trade.tariff.help', { max: MAX_TARIFF_RATE })} />
      <Btn variant="primary" block busy={cmd.pending}
        onClick={() => { void cmd.run({ type: 'set_market_tariff', actorId: actor, cityId: market.cityId, rate: rate / 100 }); }}>
        {t('trade.tariff.set')}
      </Btn>
      <Inline error={cmd.error} />
    </Collapsible>
  );
}

function GuildBlock({ guild, focus }: { guild: ReturnType<typeof useGuild>; focus?: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const loaded = !!guild.data;
  useEffect(() => { if (focus && loaded) ref.current?.scrollIntoView({ block: 'center' }); }, [focus, loaded]);
  if (guild.isLoading) return <Skeleton rows={1} />;
  if (guild.isError || !guild.data) return <ErrorBox error={guild.error} onRetry={() => guild.refetch()} />;
  const g = guild.data;
  const rank = g.myRank ?? 0;
  const next = rank === 0 ? g.thresholds.journeyman : rank === 1 ? g.thresholds.master : rank === 2 ? g.thresholds.guildmaster : null;
  const xp = g.myXp ?? 0;
  return (
    <div ref={ref}>
    <Collapsible title={t('trade.guild.title', { name: g.cityName })} summary={t(`trade.guild.rank.${rank}`)} defaultOpen={xp > 0 || !!focus}>
      <div className="tr-guild-me">
        <div className="tr-guild-top"><strong>{t('eco.guild.yourRank', { rank: t(`trade.guild.rank.${rank}`) })}</strong><span className="num">{xp} xp</span></div>
        {next != null ? (
          <>
            <span className="bar"><span className="bar-fill gold" style={{ width: `${clamp01(xp / next) * 100}%` }} /></span>
            <span className="tr-help">{t('eco.guild.toNext', { xp, next, rank: t(`trade.guild.rank.${rank + 1}`) })}</span>
          </>
        ) : <span className="tr-help">{t('eco.guild.top')}</span>}
      </div>
      {g.nextElectionDay != null && <p className="tr-help">{t('trade.guild.nextElection')}: {formatDate(g.nextElectionDay)}</p>}
      <p className="tr-help">{t('trade.guild.thresholds', { journeyman: g.thresholds.journeyman, master: g.thresholds.master, guildmaster: g.thresholds.guildmaster })}</p>
      {g.ladder.length > 0 && (
        <div className="tr-list">
          {g.ladder.map((row, i) => (
            <Row key={row.houseId} lead={<span className="tr-pos">{i + 1}</span>} title={row.houseName}
              meta={<Badge tone={row.rank >= 3 ? 'gold' : 'plain'}>{t(`trade.guild.rank.${row.rank}`)}</Badge>}
              trail={<span className="num tr-strong">{row.xp} xp</span>} />
          ))}
        </div>
      )}
    </Collapsible>
    </div>
  );
}
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

function ElectionBlock({ cityId, election, actor }: { cityId: Id; election: NonNullable<TitleView['election']>; actor: Id }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  return (
    <Collapsible title={t('trade.election.title')} summary={formatDate(election.nextElectionDay)}>
      <p className="tr-help">{t('trade.election.next')}: {formatDate(election.nextElectionDay)} · {t('trade.election.term')}: {election.termDays}</p>
      <div className="tr-list">
        {election.candidates.map((c) => (
          <Row key={c.characterId}
            title={<>{c.name}{election.myVote === c.characterId && <Tag tone="gold">{t('trade.election.myVote')}</Tag>}</>}
            meta={<Meta items={[c.houseName, `${c.votes} ${t('trade.election.votes').toLowerCase()}`]} />}
            trail={election.myFranchise && (
              <Btn small variant={election.myVote === c.characterId ? 'quiet' : 'primary'} busy={cmd.pending}
                onClick={() => { void cmd.run({ type: 'cast_elective_vote', actorId: actor, titleId: cityId, candidateId: c.characterId }); }}>
                {t('trade.election.vote')}
              </Btn>
            )} />
        ))}
      </div>
      {!election.myFranchise && <p className="tr-help">{t('trade.election.noFranchise')}</p>}
      <Inline error={cmd.error} />
    </Collapsible>
  );
}
